/**
 * /mail/suppression — customer-side suppression management (OCP-132).
 *
 * Two operations:
 *   - LIST + filter by reason + email substring
 *   - DELETE one row (with confirmation modal)
 *
 * Manual-add (POST) is "coming soon" until OMS-33 lands. Renders an
 * info bar at the top explaining what suppression IS — most
 * customers don't know what they're looking at the first time.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { MailReadOnlyNotice } from '../components/MailReadOnlyNotice';
import { ApiError, api } from '../lib/api';
import { useMailWriteAccess } from '../lib/use-mail-write-access';
import { fmtDate } from '../lib/format-time';

interface SuppressionRow {
  id: string;
  email: string;
  reason: string | null;
  added_at: string | null;
  added_by: string | null;
}

interface ListResponse {
  oms_configured: boolean;
  customer_exists: boolean;
  suppression: SuppressionRow[];
  cursor: string | null;
}

const REASON_OPTIONS = ['', 'bounce', 'complaint', 'manual'] as const;

export function MailSuppressionPage() {
  const { t } = useTranslation('mail-suppression');
  const [params, setParams] = useSearchParams();
  const [reason, setReason] = useState(params.get('reason') ?? '');
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [allRows, setAllRows] = useState<SuppressionRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [response, setResponse] = useState<ListResponse | null>(null);
  const [state, setState] = useState<'loading' | 'idle' | 'error'>('loading');
  const [errorDetail, setErrorDetail] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  // OCP-545 - suppression entries decide who the account can still mail:
  // owner/admin only, same as the rest of the mail setup.
  const { canWrite } = useMailWriteAccess();
  const [actionError, setActionError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    const p = new URLSearchParams();
    if (reason) p.set('reason', reason);
    if (email) p.set('email', email);
    setParams(p, { replace: true });
    setCursor(undefined);
    setAllRows([]);
  }, [reason, email, setParams]);

  const qs = useMemo(() => {
    const p = new URLSearchParams();
    if (reason) p.set('reason', reason);
    if (email) p.set('email', email);
    if (cursor) p.set('cursor', cursor);
    p.set('limit', '50');
    return p.toString();
  }, [reason, email, cursor]);

  const load = useCallback(async () => {
    setState('loading');
    setErrorDetail(null);
    try {
      const r = await api<ListResponse>(`/v1/portal/mail/suppression?${qs}`);
      setResponse(r);
      setAllRows((prev) => (cursor ? [...prev, ...r.suppression] : r.suppression));
      setNextCursor(r.cursor);
      setState('idle');
    } catch (err) {
      if (err instanceof ApiError) {
        setErrorDetail(
          err.status === 502 ? t('error.unreachable') : t('error.loadHttp', { status: err.status }),
        );
      } else {
        setErrorDetail(t('error.load'));
      }
      setState('error');
    }
  }, [qs, cursor, t]);

  useEffect(() => {
    void load();
  }, [load]);

  async function doAdd(email: string): Promise<void> {
    setActionError(null);
    try {
      const row = await api<SuppressionRow>('/v1/portal/mail/suppression', {
        method: 'POST',
        body: { email, reason: 'manual' },
      });
      setAddOpen(false);
      // Prepend the new row so the customer sees it immediately.
      setAllRows((prev) => [row, ...prev.filter((r) => r.id !== row.id)]);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setActionError(t('error.invalidEmail'));
      } else {
        setActionError(t('error.add'));
      }
    }
  }

  async function doDelete(id: string): Promise<void> {
    setActionError(null);
    try {
      await api(`/v1/portal/mail/suppression/${id}`, { method: 'DELETE' });
      setConfirmId(null);
      // Optimistic: drop the row from local state instead of re-fetching.
      setAllRows((prev) => prev.filter((r) => r.id !== id));
    } catch (err) {
      if (err instanceof ApiError) {
        setActionError(
          err.status === 404 ? t('error.gone') : t('error.removeHttp', { status: err.status }),
        );
      } else {
        setActionError(t('error.remove'));
      }
    }
  }

  if (response && !response.oms_configured) {
    return (
      <div className="rounded-lg border border-amber-200 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-900/30 p-5 text-sm text-amber-900 dark:text-amber-200">
        <h2 className="text-base font-semibold">{t('unavailable.title')}</h2>
        <p className="mt-1">{t('unavailable.text')}</p>
      </div>
    );
  }

  return (
    <div>
      <InfoBar />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-gray-600 dark:text-gray-400">{t('filters.reason')}</span>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          >
            {REASON_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {o ? t(`reason.${o}`, { defaultValue: o }) : t('filters.all')}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs">
          <span className="text-gray-600 dark:text-gray-400">{t('filters.email')}</span>
          <input
            type="search"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('filters.emailPlaceholder')}
            className="w-56 rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          />
        </label>
        {canWrite && (
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600"
          >
            {t('filters.add')}
          </button>
        )}
      </div>

      <MailReadOnlyNotice />

      {actionError && (
        <div
          role="alert"
          className="mb-4 rounded border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300"
        >
          {actionError}
        </div>
      )}
      {state === 'error' && errorDetail && (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300"
        >
          {errorDetail}
        </div>
      )}

      {state === 'idle' && allRows.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">{t('table.email')}</th>
                <th className="px-3 py-2">{t('table.reason')}</th>
                <th className="px-3 py-2">{t('table.added')}</th>
                <th className="px-3 py-2">{t('table.by')}</th>
                <th className="px-3 py-2 text-right">{t('table.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {allRows.map((row) => (
                <tr key={row.id}>
                  <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">{row.email}</td>
                  <td className="px-3 py-2">
                    <ReasonPill reason={row.reason ?? null} />
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {row.added_at ? formatDate(row.added_at) : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">
                    {row.added_by ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canWrite ? (
                      <button
                        type="button"
                        onClick={() => setConfirmId(row.id)}
                        className="text-xs text-red-700 dark:text-red-300 hover:underline"
                      >
                        {t('table.unsuppress')}
                      </button>
                    ) : (
                      <span className="text-xs text-gray-400">{t('table.readOnly')}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {nextCursor && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            disabled={state === 'loading'}
            onClick={() => setCursor(nextCursor)}
            className="rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:bg-gray-100 dark:disabled:bg-gray-800/60"
          >
            {state === 'loading' ? t('loading') : t('loadMore')}
          </button>
        </div>
      )}

      {confirmId && (
        <ConfirmModal
          email={allRows.find((r) => r.id === confirmId)?.email ?? ''}
          onCancel={() => setConfirmId(null)}
          onConfirm={() => doDelete(confirmId)}
        />
      )}
      {addOpen && (
        <AddModal
          onCancel={() => setAddOpen(false)}
          onAdd={(email) => void doAdd(email)}
        />
      )}
    </div>
  );
}

function AddModal({
  onCancel,
  onAdd,
}: {
  onCancel: () => void;
  onAdd: (email: string) => void;
}) {
  const { t } = useTranslation('mail-suppression');
  const [email, setEmail] = useState('');
  const trimmed = email.trim();
  const looksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-suppression-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-md rounded-lg bg-white dark:bg-gray-900 p-5 shadow-xl">
        <h2 id="add-suppression-title" className="text-base font-semibold">
          {t('addModal.title')}
        </h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{t('addModal.text')}</p>
        <label className="mt-3 block">
          <span className="block text-xs font-medium text-gray-700 dark:text-gray-300">{t('addModal.email')}</span>
          <input
            type="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('addModal.placeholder')}
            className="mt-1 w-full rounded border border-gray-300 dark:border-gray-700 px-2 py-1.5 text-sm"
          />
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            {t('addModal.cancel')}
          </button>
          <button
            type="button"
            onClick={() => onAdd(trimmed)}
            disabled={!looksValid}
            className="rounded bg-brand-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-600 disabled:bg-gray-300"
          >
            {t('addModal.add')}
          </button>
        </div>
      </div>
    </div>
  );
}

function InfoBar() {
  const { t } = useTranslation('mail-suppression');
  return (
    <div className="mb-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 p-3 text-xs text-gray-700 dark:text-gray-300">
      <p>
        <strong>{t('info.lead')}</strong> {t('info.text')}
      </p>
    </div>
  );
}

function ReasonPill({ reason }: { reason: string | null }) {
  const { t } = useTranslation('mail-suppression');
  if (!reason) return <span className="text-xs text-gray-400">—</span>;
  const tone =
    reason === 'complaint'
      ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-900 dark:text-amber-200 border-amber-200 dark:border-amber-900/50'
      : reason === 'bounce'
        ? 'bg-red-50 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-900/50'
        : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-800';
  return (
    <span className={`inline-flex rounded border px-1.5 py-0.5 text-xs ${tone}`}>
      {t(`reason.${reason}`, { defaultValue: reason })}
    </span>
  );
}

function EmptyState() {
  const { t } = useTranslation('mail-suppression');
  return (
    <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 text-center text-sm text-gray-500">
      {t('empty')}
    </div>
  );
}

function ConfirmModal({
  email,
  onCancel,
  onConfirm,
}: {
  email: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation('mail-suppression');
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="suppression-confirm-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-md rounded-lg bg-white dark:bg-gray-900 p-5 shadow-xl">
        <h2 id="suppression-confirm-title" className="text-base font-semibold">
          {t('confirm.title', { email })}
        </h2>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{t('confirm.text')}</p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/60"
          >
            {t('confirm.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700"
          >
            {t('confirm.unsuppress')}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  return fmtDate(iso, iso);
}
